import { Injectable } from '@angular/core';
import { filterNonEmptyArray } from '@app/core/utils/rxjs.util';
import { localeCompare } from '@app/shared/utils/array.utils';
import { State } from '@app/store';
import { Store } from '@ngrx/store';
import cloneDeep from 'lodash.clonedeep';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServiceService } from '@app/core/services/service.service';
import { Hotel } from '@app/core/models/user.interface';
import {
  HotelSetting,
  ServiceId,
} from '@app/core/async-services/http/versioned/interfaces/permissions.interface';
import { HotelSettingsService } from '../hotel-settings.service';
import { selectAllHotels } from '@app/store/user/user.selectors';

@Injectable({
  providedIn: 'root',
})
export class HotelsService {
  constructor(
    private _store: Store<State>,
    private _serviceService: ServiceService,
    private _hotelSettingsService: HotelSettingsService,
  ) {}

  get hotels$(): Observable<Hotel[]> {
    return this._store.select(selectAllHotels).pipe(
      filterNonEmptyArray(),
      map((hotels) => cloneDeep(hotels).sort(localeCompare('entName'))),
    );
  }

  get taskHotels$(): Observable<Hotel[]> {
    return combineLatest([
      this._getHotelsWithService$(ServiceId.TASKS),
      this._getHotelsWithSetting('CAN_CREATE_TASKS_ON_PUBLISHING_BOARDS'),
    ]).pipe(
      map(([hotelsWithGeneralTasks, hotelsWithRoomCleanTasks]) => {
        const allHotels = [
          ...hotelsWithGeneralTasks,
          ...hotelsWithRoomCleanTasks,
        ];
        const uniqueHotels = allHotels.reduce<Hotel[]>((hotels, hotel) => {
          if (hotels.every(({ entId }) => entId !== hotel.entId)) {
            return [...hotels, hotel];
          }
          return hotels;
        }, []);
        return uniqueHotels.sort(localeCompare('entName'));
      }),
    );
  }

  get realtimeRoomsHotels$(): Observable<Hotel[]> {
    return this.hotels$.pipe(
      map((hotels) => {
        const entIds = new Set<string>();
        this._serviceService.hotelsWithRealtimeRoomsService.forEach((entId) => {
          entIds.add(entId);
        });
        return hotels.filter((hotel) => entIds.has(hotel.entId));
      }),
    );
  }

  private _getHotelsWithService$(serviceId: ServiceId): Observable<Hotel[]> {
    return combineLatest([
      this.hotels$,
      this._serviceService.gethotelIdsWithService(serviceId),
    ]).pipe(
      map(([hotels, entIds]) =>
        hotels.filter((hotel) => entIds.includes(hotel.entId)),
      ),
    );
  }

  private _getHotelsWithSetting(setting: HotelSetting): Observable<Hotel[]> {
    return combineLatest([
      this.hotels$,
      this._hotelSettingsService.getHotelIdsWithSetting(setting),
    ]).pipe(
      map(([hotels, entIds]) =>
        hotels.filter((hotel) => entIds.includes(hotel.entId)),
      ),
    );
  }
}
